import { floorSurfaceY } from "../interior/layout.js";
import { ElevatorSim } from "./scheduler.js";
import type { SimEvent } from "./scheduler.js";
import { PASSABLE_OPEN, SHAFT, doorBlock, bankToLocal } from "./dims.js";
import type { CabinSlot } from "./dims.js";
import { QUICK, TIMING, floorAtHeight } from "./timing.js";
import type { Timing } from "./timing.js";
import type { Collider } from "../camera/walk.js";

/** What the player's locomotion reads while riding (written by the elevator tick, read by the rig). */
export const ride = { active: false, y: 0 };

export type PanelKind = "hall" | "car";
export interface Near {
  kind: PanelKind;
  cabin: number;
}

const surfaceY = floorSurfaceY;

const PHASE_CODE = { idle: 0, opening: 1, open: 2, closing: 3, moving: 4 } as const;

/** `prefers-reduced-motion` shortens the doors and the ride. */
export function reducedMotion(): boolean {
  try {
    return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  } catch {
    return false;
  }
}

/**
 * The elevator bank of one building as the client sees it: the simulation, where the player
 * stands relative to the cabins, the panel the player is using and what the rig needs each frame
 * (door blockers, the ride height). One per building, created when its interior opens.
 */
export class ElevatorStore {
  readonly sim: ElevatorSim;
  readonly ys: number[];
  /** The player stands in this cabin's footprint (per cabin; latched while it moves). */
  readonly inCab: boolean[];
  /** The cabin the player rides in (-1 none). */
  rider = -1;
  /** The panel within reach, and the one the HUD has open. */
  near: Near | null = null;
  panel: Near | null = null;
  /** Index of the floor whose interior is drawn. */
  drawn = 0;
  /** Per cabin: redraw the signs and move the doors. Set by the bank views. */
  readonly views: Array<(() => void) | undefined> = [];
  onSound: ((e: SimEvent, cabin: number, floor: number) => void) | null = null;
  /** The last events of the simulation with the door and height at that moment (tests read it). */
  readonly log: Array<{
    e: SimEvent;
    cabin: number;
    floor: number;
    door: number;
    y: number;
    t: number;
  }> = [];
  /** Time scale of the clock (tests and screenshots slow it down; 1 in play). */
  speed = 1;
  /** Slow the clock to this factor when a cabin arrives (screenshots catch the doors opening). */
  slowOnArrive: number | null = null;
  private listeners = new Set<() => void>();
  private version = 0;
  private sig = 0;
  private blockKey = "";
  private blocks: Collider[] = [];

  constructor(
    readonly levels: readonly number[],
    readonly slots: readonly CabinSlot[],
    readonly timing: Timing = TIMING,
  ) {
    this.ys = levels.map(surfaceY);
    this.sim = new ElevatorSim(this.ys, slots.length, timing);
    this.inCab = slots.map(() => false);
    this.sim.onEvent = (e, c, f) => {
      const cab = this.sim.cabins[c]!;
      this.log.push({
        e,
        cabin: c,
        floor: this.levels[f] ?? f,
        door: Math.round(cab.door * 1000) / 1000,
        y: Math.round(cab.y * 1000) / 1000,
        t: Math.round(this.sim.time * 1000) / 1000,
      });
      if (this.log.length > 300) this.log.splice(0, 100);
      if (e === "arrive" && this.slowOnArrive !== null) {
        this.speed = this.slowOnArrive;
        this.slowOnArrive = null;
      }
      this.onSound?.(e, c, f);
    };
  }

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  getVersion = (): number => this.version;
  private bump(): void {
    this.version++;
    for (const l of this.listeners) l();
  }

  indexOfLevel(level: number): number {
    return this.levels.indexOf(level);
  }

  // ------------------------------------------------------------------ the clock

  /**
   * One frame: finds where the player is relative to the cabins, steps the simulation, and
   * reports the floor to draw when the player rides past a midpoint. `p` is the player's position
   * in the building's frame (null when nobody walks there, e.g. the cutaway).
   */
  tick(
    dt: number,
    p: { x: number; y: number; z: number } | null,
    setLevel: (level: number) => void,
    carry: (y: number) => void,
  ): void {
    const sim = this.sim;
    let near: Near | null = null;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      const c = sim.cabins[i]!;
      let inside = false;
      let doorway = false;
      const moving = c.phase === "moving";
      if (p) {
        const dx = p.x - slot.x;
        const dz = p.z - slot.z;
        const cs = Math.cos(slot.yaw);
        const sn = Math.sin(slot.yaw);
        const lx = dx * cs - dz * sn;
        const lz = dx * sn + dz * cs;
        const dy = Math.abs(p.y - c.y);
        inside = Math.abs(lx) < 0.97 && Math.abs(lz) < 0.97 && dy < 0.7;
        doorway = Math.abs(lx) < 0.8 && lz > 0.75 && lz < 1.55 && dy < 0.8;
        if (!near) {
          const level = Math.abs(p.y - (this.ys[this.drawn] ?? 0)) < 1;
          if (inside && !moving) near = { kind: "car", cabin: i };
          else if (level && Math.abs(lx) < 1.9 && lz > 1.1 && lz < 2.6)
            near = { kind: "hall", cabin: i };
        }
      }
      // While a cabin moves, whether the player is in it stays as it was when it left.
      if (moving) {
        c.blocked = false;
        continue;
      }
      this.inCab[i] = inside;
      c.blocked = doorway;
    }
    // The cabin the player rides in keeps `near` on its panel while it moves.
    if (this.rider >= 0 && sim.cabins[this.rider]!.phase === "moving")
      near = { kind: "car", cabin: this.rider };
    // Step in small chunks so that a long frame cannot carry a cabin (and its rider) past a floor
    // without the swap and the arrival being seen.
    let left = dt * this.speed;
    while (left > 1e-9) {
      const chunk = Math.min(left, 1 / 20);
      left -= chunk;
      sim.step(chunk);
      let rider = -1;
      for (let i = 0; i < this.slots.length; i++) {
        if (this.inCab[i] && sim.cabins[i]!.phase === "moving") rider = i;
      }
      if (rider >= 0) {
        const y = sim.cabins[rider]!.y;
        ride.y = y;
        const idx = floorAtHeight(this.ys, y, this.drawn);
        if (idx !== this.drawn) {
          this.drawn = idx;
          setLevel(this.levels[idx]!);
        }
      } else if (this.rider >= 0) {
        // The ride ended: the player stands at the height of the floor the cabin stopped at.
        carry(sim.cabins[this.rider]!.y);
      }
      this.rider = rider;
    }
    ride.active = this.rider >= 0;
    for (const v of this.views) v?.();
    // Tell React only when something it shows changed.
    let sig = 7;
    for (const c of sim.cabins) {
      sig = (Math.imul(sig, 31) + PHASE_CODE[c.phase] * 97 + c.at * 13 + (c.dir + 1) * 5) | 0;
      for (let f = 0; f < c.car.length; f++) sig = (Math.imul(sig, 3) + (c.car[f] ? 1 : 0)) | 0;
    }
    for (let f = 0; f < sim.up.length; f++)
      sig = (Math.imul(sig, 5) + (sim.up[f] ? 1 : 0) + (sim.down[f] ? 2 : 0)) | 0;
    const nearChanged =
      (near?.kind ?? "") !== (this.near?.kind ?? "") ||
      (near?.cabin ?? -1) !== (this.near?.cabin ?? -1);
    if (nearChanged) {
      this.near = near;
      if (
        !near ||
        (this.panel && (this.panel.kind !== near.kind || this.panel.cabin !== near.cabin))
      )
        this.panel = null;
    }
    if (nearChanged || sig !== this.sig) {
      this.sig = sig;
      this.bump();
    }
  }

  // ------------------------------------------------------------------ the player's buttons

  /** The panel to open with E or a tap: the one within reach. */
  openPanel(near: Near | null = this.near): boolean {
    if (!near) return false;
    this.panel = near;
    this.bump();
    return true;
  }
  closePanel(): void {
    if (!this.panel) return;
    this.panel = null;
    this.bump();
  }

  /** Calls the elevator from the floor drawn. */
  callHall(dir: 1 | -1): number {
    return this.sim.callHall(this.drawn, dir);
  }
  pressFloor(cabin: number, index: number): boolean {
    return this.sim.pressCar(cabin, index);
  }
  pressDoor(cabin: number, what: "open" | "close"): void {
    this.sim.pressDoor(cabin, what);
  }

  // ------------------------------------------------------------------ the rig

  /** The closed doors as solids, for the walker on floor index `idx` (the shaft walls are in the room). */
  blockers(idx: number): Collider[] {
    const top = this.ys[idx]! + 3.4;
    let key = `${idx}`;
    for (let i = 0; i < this.slots.length; i++) key += this.doorOpenAt(i, idx) ? "o" : "c";
    if (key === this.blockKey) return this.blocks;
    this.blockKey = key;
    this.blocks = this.slots.flatMap((slot, i) =>
      this.doorOpenAt(i, idx) ? [] : [{ ...doorBlock(slot), top }],
    );
    return this.blocks;
  }

  /** Is cabin `i` at floor index `idx` with its doors open enough to walk through? */
  doorOpenAt(i: number, idx: number): boolean {
    const c = this.sim.cabins[i]!;
    return c.phase !== "moving" && c.at === idx && c.door >= PASSABLE_OPEN;
  }

  /** Where the player stands to board cabin `i`, and the yaw that faces its doors. */
  boardPose(i: number): { x: number; z: number; yaw: number } {
    const slot = this.slots[i]!;
    const [x, z] = bankToLocal(slot, 0, -0.15);
    return { x, z, yaw: slot.yaw + Math.PI };
  }

  /** Docks the cabin that gets to floor `idx` first, doors open. Returns its index. */
  board(idx: number): number {
    let best = 0;
    let eta = Infinity;
    for (const c of this.sim.cabins) {
      const e = this.sim.eta(c, idx, 0);
      if (e < eta - 1e-9) {
        eta = e;
        best = c.index;
      }
    }
    this.sim.dockAt(best, idx);
    return best;
  }

  /** The cabin the player stands in (-1 none). */
  cabinOfPlayer(): number {
    return this.inCab.findIndex(Boolean);
  }

  /** Resolves when the cabin has arrived at `idx` (or after `ms`). */
  arrived(cabin: number, idx: number, ms = 40_000): Promise<void> {
    return new Promise((resolve) => {
      const t0 = Date.now();
      const poll = () => {
        const c = this.sim.cabins[cabin]!;
        const done =
          c.at === idx && c.phase !== "moving" && c.phase !== "idle" && c.phase !== "closing";
        if (done || Date.now() - t0 > ms) resolve();
        else setTimeout(poll, 80);
      };
      poll();
    });
  }

  /** A snapshot for tests and the screenshot tool (`__uzElevator`). */
  snapshot() {
    const sim = this.sim;
    return {
      levels: [...this.levels],
      drawn: this.drawn,
      rider: this.rider,
      near: this.near,
      panel: this.panel,
      quick: this.timing === QUICK,
      up: [...sim.up],
      down: [...sim.down],
      cabins: sim.cabins.map((c, i) => ({
        index: i,
        phase: c.phase,
        floor: this.levels[c.at] ?? 0,
        to: this.levels[c.to] ?? 0,
        dir: c.dir,
        door: Math.round(sim.doorAt(i) * 1000) / 1000,
        y: Math.round(c.y * 1000) / 1000,
        lit: this.levels.filter((_, f) => c.car[f]),
        reopens: c.reopens,
        inside: this.inCab[i]!,
      })),
    };
  }

  dimensions() {
    return SHAFT;
  }
}

// ------------------------------------------------------------------ registry

/** One store per building with its interior open. */
export const elevators = new Map<string, ElevatorStore>();

/** The store of a building, created (or replaced when its floors changed) for the given bank. */
export function elevatorFor(
  id: string,
  levels: readonly number[],
  slots: readonly CabinSlot[],
): ElevatorStore {
  const have = elevators.get(id);
  const same =
    have &&
    have.levels.length === levels.length &&
    have.levels.every((l, i) => l === levels[i]) &&
    have.slots.length === slots.length;
  if (same) return have;
  const next = new ElevatorStore(levels, slots, reducedMotion() ? QUICK : TIMING);
  elevators.set(id, next);
  return next;
}
